import { Link } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { useRootSearch } from '@/hooks/use-root-search'
import { ArrowDownWideNarrowIcon, ArrowUpNarrowWideIcon } from 'lucide-react'
import { IndicatorIcon } from './indicator-icon'
import { indicatorPalette, type IndicatorKind } from './indicator-colors'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'

interface SortButtonProps {
  icon: React.ReactNode
  label: string
  isActive: boolean
  sortParam: string
  sortValue?: string
  /** Indicator kind whose palette colours the active/inactive states. */
  kind: IndicatorKind
  disabled?: boolean
  disabledReason?: string
}

const DISABLED_STATE = 'border border-transparent bg-muted/50 text-muted-foreground/50 cursor-not-allowed'

const buildSortState = (kind: IndicatorKind) => {
  const p = indicatorPalette[kind]
  return {
    active: cn(p.activeBg, p.activeText, 'border', p.activeBorder),
    inactive: cn('bg-muted text-muted-foreground border border-transparent', p.hoverBg, p.hoverText),
    disabled: DISABLED_STATE,
  }
}

function SortButton(props: SortButtonProps) {
  const colors = buildSortState(props.kind)

  if (props.disabled) {
    const button = (
      <span
        className={cn('flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium', colors.disabled)}
        aria-disabled
      >
        {props.icon}
        <span>{props.label}</span>
      </span>
    )

    if (props.disabledReason) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-[200px] text-xs">
            {props.disabledReason}
          </TooltipContent>
        </Tooltip>
      )
    }

    return button
  }

  return (
    <Link
      to="."
      search={(prev) => ({
        ...prev,
        [props.sortParam]: props.isActive ? undefined : props.sortValue,
      })}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors cursor-pointer',
        props.isActive ? colors.active : colors.inactive,
      )}
    >
      {props.icon}
      <span>{props.label}</span>
    </Link>
  )
}

interface SortDirectionButtonProps {
  icon: React.ReactNode
  label: string
  isActive: boolean
  direction: 'asc' | 'desc'
  kind: IndicatorKind
}

function SortDirectionButton(props: SortDirectionButtonProps) {
  const colors = buildSortState(props.kind)

  return (
    <Link
      to="."
      search={(prev) => ({ ...prev, od: props.direction })}
      title={props.label}
      aria-label={props.label}
      className={cn(
        'flex items-center justify-center rounded-md p-1 transition-colors cursor-pointer',
        props.isActive ? colors.active : colors.inactive,
      )}
    >
      {props.icon}
    </Link>
  )
}

export function SortByAdjustButtons(props?: {
  specificDisabled?: boolean
  globalDisabled?: boolean
  specificDisabledReason?: string
  globalDisabledReason?: string
}) {
  const { t } = useLingui()
  const { type, o: sortValue, od: directionValue } = useRootSearch()

  const directionKind: IndicatorKind = sortValue === 'global' ? 'global' : type
  // Default to descending so the toggle reflects existing behaviour.
  const isAsc = directionValue === 'asc'

  const specificDisabled = props?.specificDisabled ?? false
  const globalDisabled = props?.globalDisabled ?? false
  // Hide direction toggle if the currently-active sort got disabled.
  const showDirection =
    !!sortValue && !((sortValue === 'adjust' && specificDisabled) || (sortValue === 'global' && globalDisabled))

  return (
    <>
      <SortButton
        icon={<IndicatorIcon kind={type} inherit />}
        label={t`Specific adjustment`}
        isActive={sortValue === 'adjust'}
        sortParam="o"
        sortValue="adjust"
        kind={type}
        disabled={specificDisabled}
        disabledReason={props?.specificDisabledReason}
      />
      <SortButton
        icon={<IndicatorIcon kind="global" inherit />}
        label={t`Global adjustment`}
        isActive={sortValue === 'global'}
        sortParam="o"
        sortValue="global"
        kind="global"
        disabled={globalDisabled}
        disabledReason={props?.globalDisabledReason}
      />
      {showDirection ? (
        <div className="ml-1 flex items-center gap-1">
          <SortDirectionButton
            icon={<ArrowDownWideNarrowIcon className="size-3.5" />}
            label={t`Descending`}
            isActive={!isAsc}
            direction="desc"
            kind={directionKind}
          />
          <SortDirectionButton
            icon={<ArrowUpNarrowWideIcon className="size-3.5" />}
            label={t`Ascending`}
            isActive={isAsc}
            direction="asc"
            kind={directionKind}
          />
        </div>
      ) : null}
    </>
  )
}
